import React, { useEffect, useState } from 'react';
import { Box, Card, Typography } from '@mui/material';
import type { MenuItem } from '../types';
import { useLang, pickLocale, pickDescLocale } from '../i18n/LanguageProvider';
import { palette } from '../theme';

interface Props {
  item: MenuItem;
  /** Category name — used to pick a stock food image when item.image_url fails. */
  categoryName?: string;
  onClick(item: MenuItem): void;
  onAdd(item: MenuItem): void;
}

const fmt = (n: number) => `${n.toFixed(2).replace('.', ',')} EUR`;

// Curated category → Unsplash food photo, fallback when item.image_url breaks.
const CATEGORY_IMAGES: Record<string, string> = {
  'crispy rolls':           'https://images.unsplash.com/photo-1617196034796-73dfa7b1fd56',
  'inside out':             'https://images.unsplash.com/photo-1617196701537-7329482cc9fe',
  'inside-out special':     'https://images.unsplash.com/photo-1611143669185-af224c5e3252',
  'maki':                   'https://images.unsplash.com/photo-1611143669185-af224c5e3252',
  'nigiri':                 'https://images.unsplash.com/photo-1564489563601-c53cfc451e93',
  'sashimi':                'https://images.unsplash.com/photo-1534482421-64566f976cfa',
  'poke bowls':             'https://images.unsplash.com/photo-1546069901-ba9599a7e63c',
  'sushi box':              'https://images.unsplash.com/photo-1583623025817-d180a2221d0a',
  'burgers':                'https://images.unsplash.com/photo-1568901346375-23c9450c58cd',
  'pizza':                  'https://images.unsplash.com/photo-1574071318508-1cdbab80d002',
  'pizza star':             'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38',
  'pizza starter':          'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38',
  'pasta':                  'https://images.unsplash.com/photo-1473093295043-cdd812d0e601',
  'shawarma':               'https://images.unsplash.com/photo-1561651823-34feb02250e4',
  'soups':                  'https://images.unsplash.com/photo-1547592180-85f173990554',
  'starters':               'https://images.unsplash.com/photo-1541544741938-0af808871cc0',
  'salads':                 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd',
  'desserts':               'https://images.unsplash.com/photo-1551024506-0bccd828d307',
  'desserts & daifuku mochis': 'https://images.unsplash.com/photo-1607478900766-efe13248b125',
  'beverages':              'https://images.unsplash.com/photo-1544145945-f90425340c7e',
  'drinks':                 'https://images.unsplash.com/photo-1544145945-f90425340c7e',
  'extras':                 'https://images.unsplash.com/photo-1565958011703-44f9829ba187',
  'extra dips':             'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136',
};
const DEFAULT_FALLBACK = 'https://images.unsplash.com/photo-1504674900247-0877df9cc836';
function categoryImage(category?: string): string {
  const key = (category || '').toLowerCase().trim();
  return `${CATEGORY_IMAGES[key] || DEFAULT_FALLBACK}?w=600&auto=format&q=75`;
}

export const MenuItemCard: React.FC<Props> = ({ item, categoryName, onClick, onAdd }) => {
  const { lang } = useLang();
  const name = pickLocale(item, lang);
  const desc = pickDescLocale(item, lang);

  // 0 → item.image_url, 1 → category Unsplash, 2 → emoji placeholder
  const [stage, setStage] = useState<0 | 1 | 2>(item.image_url ? 0 : 1);
  useEffect(() => { setStage(item.image_url ? 0 : 1); }, [item.image_url, categoryName]);

  return (
    <Card
      onClick={() => onClick(item)}
      className="lift"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        cursor: 'pointer',
        position: 'relative',
        border: `1px solid ${palette.border}`,
        backgroundColor: palette.bgPanel,
        borderRadius: '16px',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
        '&:hover': {
          borderColor: palette.borderHover,
          transform: 'translateY(-4px)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.45)',
        },
      }}
    >
      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '4 / 3', backgroundColor: palette.bgElev }}>
        {stage === 0 && item.image_url && (
          <Box
            component="img"
            src={item.image_url}
            alt={name}
            loading="lazy"
            onError={() => setStage(1)}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}
        {stage === 1 && (
          <Box
            component="img"
            src={categoryImage(categoryName)}
            alt={name}
            loading="lazy"
            onError={() => setStage(2)}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}
        {stage === 2 && (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              background: `linear-gradient(135deg, ${palette.bgElev} 0%, ${palette.bg} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '52px',
              color: `${palette.primary}88`,
            }}
          >
            🍽️
          </Box>
        )}
      </Box>

      <Box sx={{ p: { xs: 1.75, sm: 2 }, display: 'flex', flexDirection: 'column', gap: 0.75, flex: 1 }}>
        <Typography
          dir="auto"
          sx={{
            color: '#fff',
            fontWeight: 700,
            fontSize: { xs: '15px', sm: '16px' },
            lineHeight: 1.25,
            unicodeBidi: 'isolate',
          }}
        >
          {name}
        </Typography>
        {desc && (
          <Typography
            dir="auto"
            sx={{
              color: palette.textMuted,
              fontSize: '12.5px',
              lineHeight: 1.4,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              unicodeBidi: 'isolate',
              flex: 1,
            }}
          >
            {desc}
          </Typography>
        )}
        <Box
          onClick={(e) => { e.stopPropagation(); onAdd(item); }}
          sx={{
            mt: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography sx={{ color: palette.primary, fontWeight: 800, fontSize: '17px' }}>
            {fmt(Number(item.base_price) || 0)}
          </Typography>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${palette.primary} 0%, ${palette.primaryDark} 100%)`,
              color: palette.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '20px',
              lineHeight: 1,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.08)',
                boxShadow: `0 6px 16px ${palette.primary}66`,
              },
            }}
          >
            +
          </Box>
        </Box>
      </Box>
    </Card>
  );
};
